import React, { useState, useEffect } from 'react';
import { VideoHighlight, videoHighlightsService } from '../../../shared/services/videoHighlightsService';
import { VideoProcessingJob, videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';
import { formatTime } from '../../../shared/components/video-editor/utils/clipUtils';

type MatchPlayer = { player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string };

interface HighlightDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  highlight: VideoHighlight | null;
  matchPlayers?: MatchPlayer[];
  onToast?: (message: string, type: 'success' | 'error') => void;
  onRefresh?: () => void;
  onEditHighlight?: (highlightId: string) => void;
  onDeleteHighlight?: (highlightId: string, highlightName: string) => void;
}

const HighlightDetailsDrawer: React.FC<HighlightDetailsDrawerProps> = ({
  isOpen,
  onClose,
  highlight,
  matchPlayers = [],
  onToast,
  onRefresh,
  onEditHighlight,
  onDeleteHighlight,
}) => {
  const [jobs, setJobs] = useState<VideoProcessingJob[]>([]);
  const [isLoadingJob, setIsLoadingJob] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Load jobs when highlight is opened
  useEffect(() => {
    if (isOpen && highlight) {
      console.log('[HighlightDetailsDrawer] Opening drawer for highlight:', highlight.id, highlight.name);
      loadJobs();
    } else {
      setJobs([]);
    }
  }, [isOpen, highlight]);

  const loadJobs = async () => {
    if (!highlight) {
      console.warn('[HighlightDetailsDrawer] No highlight provided to loadJobs');
      return;
    }
    console.log('[HighlightDetailsDrawer] loadJobs called for highlight ID:', highlight.id);
    setIsLoadingJob(true);
    try {
      console.log('[HighlightDetailsDrawer] Calling getHighlightJobs with:', highlight.id);
      const result = await videoHighlightsService.getHighlightJobs(highlight.id);
      console.log('[HighlightDetailsDrawer] getHighlightJobs result:', {
        success: result.success,
        jobCount: result.data?.length || 0,
        error: result.error
      });
      if (result.success) {
        setJobs(result.data || []);
        console.log('[HighlightDetailsDrawer] Jobs state updated:', result.data?.length || 0, 'job(s) found');
      } else {
        console.error('[HighlightDetailsDrawer] Failed to load jobs:', result.error);
        setJobs([]);
      }
    } catch (error) {
      console.error('[HighlightDetailsDrawer] Exception in loadJobs:', error);
      setJobs([]);
    } finally {
      setIsLoadingJob(false);
    }
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleRetryJob = async (jobId: string) => {
    if (!highlight) return;
    setIsRetrying(true);
    try {
      const result = await videoProcessingJobsService.retryJob(jobId);
      if (result.success) {
        await loadJobs();
        if (onToast) {
          onToast('Job queued for retry', 'success');
        }
        onRefresh?.();
      } else if (onToast) {
        onToast(result.error || 'Failed to retry job', 'error');
      }
    } catch (error) {
      console.error('Error retrying job:', error);
      if (onToast) {
        onToast('Failed to retry job', 'error');
      }
    } finally {
      setIsRetrying(false);
    }
  };

  const getJobStatusBadge = (status: string) => {
    const styles = {
      queued: 'bg-gray-100 text-gray-800 border border-gray-200',
      running: 'bg-blue-100 text-blue-800 border border-blue-200',
      succeeded: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
    };
    return styles[status as keyof typeof styles] || styles.queued;
  };

  const getHighlightStatusBadge = (status: string) => {
    const styles = {
      saved: 'bg-gray-100 text-gray-800 border border-gray-200',
      queued: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      processing: 'bg-blue-100 text-blue-800 border border-blue-200',
      stitched: 'bg-purple-100 text-purple-800 border border-purple-200',      // NEW: Base video ready
      enhancing: 'bg-indigo-100 text-indigo-800 border border-indigo-200',     // NEW: Overlay worker running
      completed: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
    };
    return styles[status as keyof typeof styles] || styles.saved;
  };

  if (!isOpen || !highlight) return null;

  const totalDuration = highlight.segments_data?.reduce((sum, clip) => {
    return sum + (clip.endTime - clip.startTime);
  }, 0) || 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Highlight Details</h2>
            <p className="text-sm text-gray-500 mt-1">{highlight.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Name</label>
                <p className="text-sm text-gray-900 mt-1">{highlight.name}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Status</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 rounded-full text-xs ${getHighlightStatusBadge(highlight.status)}`}>
                    {highlight.status.charAt(0).toUpperCase() + highlight.status.slice(1)}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Clips</label>
                <p className="text-sm text-gray-900 mt-1">{highlight.segments_data?.length || 0} clips</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Total Duration</label>
                <p className="text-sm text-gray-900 mt-1">{formatTime(totalDuration)}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Created</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(highlight.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Last Updated</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(highlight.updated_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Associated players</label>
                <p className="text-sm text-gray-900 mt-1">
                  {(() => {
                    const ids = highlight.player_ids;
                    if (!ids?.length) return <span className="text-gray-500">Team highlight</span>;
                    const byId = new Map(matchPlayers.map((p) => [p.player_id, p.full_name]));
                    const names = ids.map((id) => byId.get(id) || 'Unknown player');
                    return names.join(', ');
                  })()}
                </p>
              </div>
            </div>
          </div>

          {/* Segments Summary */}
          {highlight.segments_data && highlight.segments_data.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Segments</h3>
              <div className="space-y-2">
                {highlight.segments_data.map((segment, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">
                        Clip {index + 1}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(segment.endTime - segment.startTime)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>
                        <span className="text-gray-500">Time:</span>{' '}
                        {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
                      </div>
                      {segment.metadata?.statType && (
                        <div>
                          <span className="text-gray-500">Event:</span> {segment.metadata.statType}
                        </div>
                      )}
                      {segment.metadata?.teamName && (
                        <div>
                          <span className="text-gray-500">Team:</span> {segment.metadata.teamName}
                        </div>
                      )}
                      {segment.metadata?.matchMinute !== undefined && (
                        <div>
                          <span className="text-gray-500">Minute:</span> {segment.metadata.matchMinute}'
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Processing Jobs */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Processing Jobs</h3>
            </div>
            {isLoadingJob ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No processing jobs found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div key={job.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${getJobStatusBadge(job.status)}`}>
                      {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                    </span>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                          {job.job_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {job.status === 'failed' && (
                    <button
                          onClick={() => handleRetryJob(job.id)}
                      disabled={isRetrying}
                      className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {isRetrying ? 'Retrying...' : 'Retry'}
                    </button>
                  )}
                </div>
                    {job.depends_on && (
                      <p className="text-xs text-gray-500 mt-1">
                        Depends on: {jobs.find(j => j.id === job.depends_on)?.job_type.replace(/_/g, ' ') || 'another job'}
                      </p>
                    )}
                {job.error_message && (
                  <p className="text-xs text-red-600 mt-1">{job.error_message}</p>
                )}
                {job.attempts > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Attempts: {job.attempts}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Created: {new Date(job.created_at).toLocaleString()}
                </p>
                {job.updated_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(job.updated_at).toLocaleString()}
                  </p>
                )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Output Info */}
          {(highlight.output_url || highlight.enhanced_url) && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Output</h3>
              <div className="space-y-3">
                {/* Enhanced Video URL (if available) */}
                {highlight.enhanced_url && (
                  <div>
                    <label className="text-xs text-gray-500">Enhanced Video URL</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                        {highlight.enhanced_url}
                      </p>
                      <button
                        onClick={() => copyToClipboard(highlight.enhanced_url || '', 'enhanced_url')}
                        className="text-blue-600 hover:text-blue-700 text-xs"
                      >
                        {copiedField === 'enhanced_url' ? 'Copied!' : 'Copy'}
                      </button>
                      <a
                        href={highlight.enhanced_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-xs"
                      >
                        View
                      </a>
                    </div>
                  </div>
                )}
                {/* Base Video URL (stitched) */}
                {highlight.output_url && (
                <div>
                    <label className="text-xs text-gray-500">Stitched Video URL</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                      {highlight.output_url}
                    </p>
                    <button
                      onClick={() => copyToClipboard(highlight.output_url || '', 'output_url')}
                      className="text-blue-600 hover:text-blue-700 text-xs"
                    >
                      {copiedField === 'output_url' ? 'Copied!' : 'Copy'}
                    </button>
                    <a
                      href={highlight.output_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-xs"
                    >
                      View
                    </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {highlight.error_message && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Error</h3>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{highlight.error_message}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 border-t border-gray-200 space-y-2">
            {/* Edit button */}
            {onEditHighlight && (
              <button
                onClick={() => {
                  if (highlight) {
                    onEditHighlight(highlight.id);
                    onClose();
                  }
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit Highlight
              </button>
            )}
            {/* Delete button - Only show if highlight has storage files */}
            {onDeleteHighlight && (() => {
              const hasStitched = Boolean(highlight.output_storage_path || highlight.output_url);
              const hasEnhanced = Boolean(highlight.enhanced_storage_path || highlight.enhanced_url);
              const hasStorageFiles = hasStitched || hasEnhanced;
              
              if (!hasStorageFiles) return null;
              
              return (
                <button
                  onClick={() => {
                    if (highlight) {
                      onDeleteHighlight(highlight.id, highlight.name);
                      onClose();
                    }
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Videos
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HighlightDetailsDrawer;

